import { openPdf } from '../../../../../../entities/procedures/pdf/shared';
import { format } from 'date-fns';
import { createPdf } from '../../../../../../../vendor/pdfMake';
import { Room } from '../../../../../../../types/Room';

// Function to format time with am/pm
const formatTimeWithAmPm = (timeStr?: string): string => {
  if (!timeStr) return '';

  // Parse time string like "07:15" or "7:15"
  const parts = timeStr.split(':');
  if (parts.length !== 2) return timeStr;

  let hours = parseInt(parts[0], 10);
  const minutes = parts[1];

  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'

  return `${hours}:${minutes}${ampm}`;
};

// Function to extract side from procedure type
const extractSideFromProcedureType = (procedureType?: string): string => {
  if (!procedureType) return '';

  const lowerType = procedureType.toLowerCase();

  // Check for patterns like "L Left" or " L " or ending with "L"
  if (
    lowerType.startsWith('l ') ||
    lowerType.includes(' l left') ||
    lowerType.includes(' l ') ||
    lowerType.endsWith(' l') ||
    lowerType.startsWith('left ') ||
    lowerType.includes(' left')
  ) {
    return 'L';
  }

  // Check for patterns like "R Right" or " R " or ending with "R"
  if (
    lowerType.startsWith('r ') ||
    lowerType.includes(' r right') ||
    lowerType.includes(' r ') ||
    lowerType.endsWith(' r') ||
    lowerType.startsWith('right ') ||
    lowerType.includes(' right')
  ) {
    return 'R';
  }

  return '';
};

const removeLateralityFromProcedureType = (procedureType?: string): string =>
  (procedureType || '')
    .replace(/\b(l|r)\s+(left|right)\b/gi, '')
    .replace(/\b(left|right)\b/gi, '')
    .replace(/\b(l|r)\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([,;:/-])/g, '$1')
    .replace(/([,;:/-])\s*$/g, '')
    .trim();

const getLastName = (name?: string): string => {
  if (!name) return '';

  const cleanName = name
    .replace(/\b(dr|md|do|pa|np|dpm|fac[a-z]*)\.?\b/gi, '')
    .replace(/[,.]/g, ' ')
    .trim();
  const nameParts = cleanName.split(/\s+/).filter(Boolean);

  return nameParts[nameParts.length - 1] || name;
};

// Function to convert time string to minutes for proper sorting
const timeToMinutes = (timeStr?: string): number => {
  if (!timeStr) return 0;

  const parts = timeStr.split(':');
  if (parts.length !== 2) return 0;

  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);

  // Handle NaN cases
  if (isNaN(hours) || isNaN(minutes)) return 0;

  return hours * 60 + minutes;
};

const getProcedureFontSize = (procedureCount: number): number => {
  if (procedureCount > 60) return 8;
  if (procedureCount > 50) return 8.5;
  if (procedureCount > 40) return 9;
  if (procedureCount > 30) return 10;
  return 11;
};

const truncateText = (text: string, maxCharacters: number): string =>
  text.length > maxCharacters ? `${text.slice(0, Math.max(maxCharacters - 3, 0)).trimEnd()}...` : text;

const lineItemColumns = {
  time: 50,
  patient: 122,
  surgeon: 76,
  side: 24,
};

const lineItemColumnGap = 4;

const getContent = (date: Date, operationRooms: Room[]) => {
  // Collect all procedures from all rooms and sort by start time
  const allProcedures = operationRooms
    .flatMap(room => room.procedures || [])
    .sort((a, b) => {
      const timeMinutesA = timeToMinutes(a.startTimeText);
      const timeMinutesB = timeToMinutes(b.startTimeText);
      return timeMinutesA - timeMinutesB;
    });

  const procedureFontSize = getProcedureFontSize(allProcedures.length);
  const headerFontSize = Math.max(procedureFontSize - 1.5, 8);

  return {
    stack: [
      {
        text: format(date, 'ddd, MMM DD'),
        alignment: 'left',
        fontSize: Math.max(procedureFontSize + 2, 13),
        bold: true,
        margin: [0, 0, 0, 5],
      },
      allProcedures.length
        ? {
            stack: [
              {
                columns: [
                  {
                    text: 'TIME',
                    width: lineItemColumns.time,
                    noWrap: true,
                  },
                  {
                    text: 'PATIENT',
                    width: lineItemColumns.patient,
                    noWrap: true,
                  },
                  {
                    text: 'SURGEON',
                    width: lineItemColumns.surgeon,
                    noWrap: true,
                  },
                  {
                    text: 'SIDE',
                    width: lineItemColumns.side,
                    noWrap: true,
                  },
                  {
                    text: 'PROCEDURE',
                    width: '*',
                    noWrap: true,
                  },
                ],
                columnGap: lineItemColumnGap,
                fontSize: headerFontSize,
                bold: true,
                color: '#555555',
                margin: [0, 0, 0, 2],
              },
              {
                canvas: [
                  {
                    type: 'line',
                    x1: 0,
                    y1: 0,
                    x2: 576,
                    y2: 0,
                    lineWidth: 0.75,
                    lineColor: '#999999',
                  },
                ],
                margin: [0, 0, 0, 3],
              },
              ...allProcedures.map((procedure, index) => {
                const side = extractSideFromProcedureType(procedure.procedureType);
                const sideColor = side === 'R' ? 'red' : side === 'L' ? 'blue' : 'black';
                const physicianName = procedure.physician?.name || procedure.physicianName || '';
                const patientName = procedure.patientNameUnformatted || procedure.patientName || '';
                const procedureType = removeLateralityFromProcedureType(procedure.procedureType);

                return {
                  stack: [
                    {
                      columns: [
                        {
                          text: formatTimeWithAmPm(procedure.startTimeText),
                          width: lineItemColumns.time,
                          noWrap: true,
                        },
                        {
                          text: truncateText(patientName, Math.floor(260 / procedureFontSize)),
                          width: lineItemColumns.patient,
                          noWrap: true,
                        },
                        {
                          text: truncateText(getLastName(physicianName), Math.floor(144 / procedureFontSize)),
                          width: lineItemColumns.surgeon,
                          noWrap: true,
                        },
                        {
                          text: side,
                          width: lineItemColumns.side,
                          color: sideColor,
                          bold: true,
                          noWrap: true,
                        },
                        {
                          text: truncateText(procedureType, Math.floor(556 / procedureFontSize)),
                          width: '*',
                          noWrap: true,
                        },
                      ],
                      columnGap: lineItemColumnGap,
                      fontSize: procedureFontSize,
                      lineHeight: 1,
                      margin: [0, 0, 0, 1],
                    },
                    index < allProcedures.length - 1 && {
                      canvas: [
                        {
                          type: 'line',
                          x1: 0,
                          y1: 0,
                          x2: 576,
                          y2: 0,
                          lineWidth: 0.25,
                          lineColor: '#eeeeee',
                        },
                      ],
                      margin: [0, 0, 0, 1],
                    },
                  ].filter(Boolean),
                };
              }),
            ],
          }
        : {
            text: 'No patients scheduled',
            fontSize: 12,
          },
    ],
  };
};

export const BeaconSchedulePdfGenerator = (
  _hospitalName: string,
  date: Date,
  _rooms: unknown, // Unused parameter kept for signature consistency with other generators
  operationRooms: Room[]
) => {
  const docDefinition = {
    pageSize: 'LETTER',
    pageOrientation: 'portrait' as const,
    pageMargins: [18, 18, 18, 18],
    defaultStyle: {
      fontSize: 12,
      lineHeight: 1,
      color: '#333333',
    },
    info: { title: `Surgery Board - Front Desk View - ${format(date, 'MM/DD/YYYY')}` },
    content: [getContent(date, operationRooms)],
  };

  openPdf(createPdf(docDefinition), `Surgery_Board_Front_Desk_${format(date, 'MM_DD_YYYY_HH_mm_ss')}`);
};
